import { useAuth, useUser } from "@clerk/expo";
import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import { useAxiosClient } from "@/hooks/useAxiosClient";
import { useLocalization } from "@/hooks/useLocalizationContext";
import { SwitchAdminRegionRequest } from "@/types/requests";
import { Region } from "@/types/resources";

interface RegionContextValue {
  region: Region;
  switching: boolean;
  switchRegion: (region: Region) => Promise<void>;
}

const RegionContext = createContext<RegionContextValue | null>(null);

const REGIONS: Region[] = ["us", "eu"];

const RegionProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { getToken } = useAuth();
  const { user } = useUser();
  const region = user?.publicMetadata.region ?? "us";
  const [switching, setSwitching] = useState(false);

  const client = useAxiosClient("/admin");

  const switchRegion = useCallback(
    async (next: Region) => {
      if (next === region) return;
      setSwitching(true);
      try {
        const data: SwitchAdminRegionRequest = { region: next };
        await client.put("/region", data);
        // Refresh the token before the user: reloading the user changes the
        // region, which triggers requests that must carry the new token.
        await getToken({ skipCache: true });
        await user?.reload();
      } finally {
        setSwitching(false);
      }
    },
    [client, getToken, region, user]
  );

  const value = useMemo(
    () => ({ region, switching, switchRegion }),
    [region, switching, switchRegion]
  );

  return (
    <RegionContext.Provider value={value}>{children}</RegionContext.Provider>
  );
};

const useRegion = (): RegionContextValue => {
  const context = useContext(RegionContext);
  if (!context) throw new Error("useRegion must be used in RegionProvider");
  return context;
};

const useRegionLabels = () => {
  const { i18n } = useLocalization();
  return useMemo(
    () => ({
      eu: {
        flagUrl: "https://flagcdn.com/w40/eu.png",
        name: i18n.t("region.eu"),
        short: "EU",
      },
      us: {
        flagUrl: "https://flagcdn.com/w40/us.png",
        name: i18n.t("region.us"),
        short: "US",
      },
    }),
    [i18n]
  );
};

export { RegionProvider, REGIONS, useRegion, useRegionLabels };
